/**
 * PATH: packages/job-hub/src/jobs/qa/QaItemEvidenceStrip.tsx
 * FILE_ID: JOBHUB-QA-EVIDENCE-STRIP-001
 * PURPOSE: Render photo thumbs and document chips on a QA checklist row.
 * WHY: Documents must not reuse the photo <img> tile (broken PDF preview).
 */
/* eslint-disable react-refresh/only-export-components -- splitter stays next to the strip */
import { QaPhotoGallery, QaPhotoLightbox } from '@quotech/ui'
import { ChevronDown, Paperclip, X } from 'lucide-react'
import { useState } from 'react'
import { formatEvidenceStamp } from './qaApiHelpers'
import type { QaEvidence } from './qaTypes'

export function splitQaEvidence(evidence: QaEvidence[]): {
  photos: QaEvidence[]
  documents: QaEvidence[]
} {
  return {
    photos: evidence.filter((ev) => ev.type !== 'note' && ev.type !== 'document'),
    documents: evidence.filter((ev) => ev.type === 'document'),
  }
}

export function QaItemEvidenceStrip({
  photos,
  documents,
  onDelete,
}: {
  photos: QaEvidence[]
  documents: QaEvidence[]
  onDelete: (evidenceId: string) => void
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  // Default EXPANDED everywhere; collapsing is a local, per-row space saver.
  // Photos always land in the report regardless of this on-screen state.
  const [collapsed, setCollapsed] = useState(false)
  if (photos.length === 0 && documents.length === 0) return null
  const galleryPhotos = photos.map((ev) => ({
    id: ev.id,
    src: ev.photo_url ?? ev.display_url ?? ev.asset_url,
    alt: ev.description || 'QA photo',
    caption: formatEvidenceStamp(ev.captured_by_name, ev.captured_at),
  }))
  return (
    <div className="mt-2 space-y-2">
      {photos.length > 0 && (
        <button
          type="button"
          onClick={() => setCollapsed((current) => !current)}
          aria-expanded={!collapsed}
          data-testid="qa-photo-strip-toggle"
          className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${collapsed ? '-rotate-90' : ''}`}
            aria-hidden
          />
          Photos ({photos.length})
        </button>
      )}
      {!collapsed && (
        <QaPhotoGallery
          photos={galleryPhotos}
          onDelete={onDelete}
          onOpen={setOpenIndex}
        />
      )}
      {openIndex != null ? (
        <QaPhotoLightbox
          photos={galleryPhotos}
          index={openIndex}
          onIndexChange={setOpenIndex}
          onClose={() => setOpenIndex(null)}
        />
      ) : null}
      <div className="flex flex-wrap gap-2">
      {documents.map((ev) => (
        <div key={ev.id} className="relative">
          {ev.asset_url ? (
            <a
              href={ev.asset_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex max-w-[12rem] items-center gap-1.5 rounded-lg border border-border bg-secondary px-2 py-1.5 text-[11px] text-foreground hover:bg-accent"
            >
              <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <span className="truncate">{ev.description || 'Attached file'}</span>
            </a>
          ) : (
            <span className="inline-flex max-w-[12rem] items-center gap-1.5 rounded-lg border border-border bg-secondary px-2 py-1.5 text-[11px]">
              <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <span className="truncate">{ev.description || 'Attached file'}</span>
            </span>
          )}
          <button
            type="button"
            onClick={() => onDelete(ev.id)}
            className="absolute -top-1.5 -right-1.5 rounded-full bg-background border border-border p-0.5"
            title="Remove file"
            aria-label="Remove file"
          >
            <X className="h-3 w-3 text-destructive" aria-hidden />
          </button>
        </div>
      ))}
      </div>
    </div>
  )
}
